import * as React from "react";

import {
  isTopicStatsRefusal,
  sessionTopicFieldRows,
  type SessionTopicRecord,
  type SessionTopicSchema,
  type SessionTopicStatsRecord,
} from "@/shared/domain/sessions";
import { useSessionTopicStats } from "@/shared/domain/sessions/hooks";
import { useSchemaFields } from "@/shared/domain/topics/hooks";
import { Banner, Select, Spinner } from "@/shared/foundation/primitives";
import { userFacingErrorMessage } from "@/shared/http";
import { pluralize } from "@/shared/text";

import styles from "./SessionTopicPanel.module.css";
import { TopicFieldStatsTable } from "./TopicFieldStatsTable";

function formatCount(count: number, noun: string): string {
  return `${count.toLocaleString()} ${pluralize(noun, count)}`;
}

export interface SessionTopicPanelProps {
  topic: SessionTopicRecord;
  sessionId: string;
  orgId: string;
  /** Whether the Topics tab shows topic stats for the session. */
  statsShown: boolean;
}

/**
 * The expanded row of a session topic: its schema's fields and, while
 * `statsShown` holds and the stats route does not refuse the session's org,
 * their stats. Fields whose stats fail to load are listed without them, under
 * the error. A topic recorded with several schemas gets a picker; stats are
 * per path across every schema, so switching schemas changes the field list
 * but not a path's numbers.
 */
export function SessionTopicPanel({
  topic,
  sessionId,
  orgId,
  statsShown,
}: SessionTopicPanelProps) {
  const [schemaId, setSchemaId] = React.useState(
    topic.schemas[0]?.schema_id ?? null,
  );
  const schema =
    topic.schemas.find((candidate) => candidate.schema_id === schemaId) ??
    topic.schemas[0] ??
    null;

  return (
    <div className={styles.root}>
      {topic.schemas.length > 1 && schema !== null && (
        <Select
          aria-label="Schema"
          size="sm"
          className={styles.schemaPicker}
          items={topic.schemas.map((candidate) => ({
            value: candidate.schema_id,
            label: candidate.name ?? candidate.schema_id,
          }))}
          value={schema.schema_id}
          onValueChange={(next) => {
            if (next !== null) {
              setSchemaId(next);
            }
          }}
        />
      )}
      {schema === null ? (
        <p className={styles.secondary}>No schema recorded</p>
      ) : (
        <SchemaFields
          topic={topic}
          schema={schema}
          sessionId={sessionId}
          orgId={orgId}
          requestStats={statsShown}
        />
      )}
    </div>
  );
}

const NO_STATS: Pick<SessionTopicStatsRecord, "fields"> = { fields: [] };

function SchemaFields({
  topic,
  schema,
  sessionId,
  orgId,
  requestStats,
}: {
  topic: SessionTopicRecord;
  schema: SessionTopicSchema;
  sessionId: string;
  orgId: string;
  requestStats: boolean;
}) {
  const fieldsQuery = useSchemaFields(schema.schema_id, orgId);
  const statsQuery = useSessionTopicStats(sessionId, topic.topic_id, {
    enabled: requestStats,
  });
  // The stats route can refuse the session's org before the run route has.
  const statsReleased = requestStats && !isTopicStatsRefusal(statsQuery.error);
  const statsError = statsReleased ? statsQuery.error : null;
  const statsShown = statsReleased && statsError === null;

  if (fieldsQuery.error) {
    return (
      <Banner variant="error">
        {userFacingErrorMessage(
          fieldsQuery.error,
          "Failed to load this topic's fields.",
        )}
      </Banner>
    );
  }
  const stats = statsShown ? statsQuery.data : NO_STATS;
  if (fieldsQuery.data === undefined || stats === undefined) {
    return <Spinner aria-label="Loading fields" />;
  }
  const rows = sessionTopicFieldRows(schema.name, fieldsQuery.data, stats);
  // A field the stats rule skips shows its reason even if its path has stats.
  const withStats = rows.filter(
    (row) => row.plan.skipReason === null && row.stats !== null,
  ).length;

  return (
    <section className={styles.fields}>
      {statsError && (
        <Banner variant="error">
          {userFacingErrorMessage(
            statsError,
            "Failed to load this topic's stats.",
          )}
        </Banner>
      )}
      <p className={styles.fieldsHeading}>
        <span>{formatCount(rows.length, "field")}</span>
        {statsShown && (
          <span className={styles.secondary}>
            {withStats.toLocaleString()} with stats
          </span>
        )}
      </p>
      <TopicFieldStatsTable rows={rows} statsShown={statsShown} />
    </section>
  );
}
